import {Injectable} from '@angular/core';
import {DATA_TYPE} from '@constants/data-type.constant';
import {ChartConstants} from '@constants/pages/chart-constants';
import {Signal} from '@models/dashboard/signal';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import { isFunction } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class ChartsHelperService {

  public static NO_STRING: string = '- No string -';

  public createHex(input: string | number, isLineChart: boolean) {
    if (isLineChart) {
      return this.createLineHex(input as number);
    } else {
      return this.createNotLineHex(input as string);
    }
  }

  public createLineHex(input: number) {
    if (!input) {
      input = 0;
    }

    let hex = input.toString(16).toUpperCase();
    while (hex.length < 6) {
      hex = '0' + hex;
    }
    return '#' + hex;
  }

  public createNotLineHex(input: string) {
    if (!input || input.isNullOrWhitespace()) {
      input = '0';
    }

    if (input.startsWith('#')) {
      return input;
    }
    let hex = parseInt(input).toString(16).toUpperCase();
    while (hex.length < 6) {
      hex = '0' + hex;
    }
    return '#' + hex;
  }

  public verticalAxisCustomRange(settings, axis) {
    axis.tickPositioner = function () {
      const positions: number[] = [];
      let min;
      if (settings.minY === 0) {
        min = settings.minY;
      } else {
        min = settings.minY || this.dataMin;
      }
      let max;
      if (settings.maxY === 0) {
        max = settings.maxY;
      } else {
        max = settings.maxY || this.dataMax;
      }
      let tick = Math.floor(min);
      const increment = Math.ceil((max - min) / ChartConstants.LINE_CHARTS_TICK_INTERVALS);
      if (increment > 0) {
        if (min !== null && max !== null) {
          for (tick; tick - increment <= max; tick += increment) {
            positions.push(tick);
          }
        }
      }
      return positions;
    };
    axis.softMin = settings.minY;
    axis.softMax = settings.maxY;
    axis.max = settings.maxY;
    axis.min = settings.minY;
    return axis;
  }


  public getSignalPrecision(machineSignal, chartSignal) {
    if (machineSignal) {
      return this.getPrecision(machineSignal.decimals, machineSignal.type);
    } else if (chartSignal) {
      return this.getPrecision(chartSignal.precision, chartSignal.signalType);
    } else {
      return 1;
    }
  }

  public getSignalUnitOrFallback(machineSignal, chartSignal) {
    let unit = '-';
    if (machineSignal && machineSignal.unit) {
      unit = machineSignal.unit;

    } else if (chartSignal && chartSignal.unit) {
      unit = chartSignal.unit;
    }
    return unit || '-';
  }

  public getSignalUnit(signal: Signal | ChartSignalEntry) {
    if (!signal.unit) {
      return '';
    }
    return '(' + signal.unit + ')';
  }

  public formatDate(date, patternStr, isLineChart: boolean, withTimeZone: boolean = false) {
    if (!patternStr) {
      patternStr = 'M/d/yyyy';
    }

    if (!isLineChart) {
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    }

    const day = date.getDate(),
        month = date.getMonth(),
        year = date.getFullYear(),
        hour = date.getHours(),
        minute = date.getMinutes(),
        second = date.getSeconds(),
        milliseconds = date.getMilliseconds(),
        h = hour % 12,
        hh = this.twoDigitPad(h),
        HH = this.twoDigitPad(hour),
        mm = this.twoDigitPad(minute),
        ss = this.twoDigitPad(second),
        dd = this.twoDigitPad(day),
        M = month + 1,
        MM = this.twoDigitPad(M),
        yyyy = year + '',
        yy = yyyy.substring(2, 2);

    // checks to see if month name will be used
    patternStr = patternStr
    .replace('hh', hh)
    .replace('h', h)
    .replace('HH', HH)
    .replace('H', hour)
    .replace('mm', mm)
    .replace('m', minute)
    .replace('ss', ss)
    .replace('s', second)
    .replace('S', milliseconds)
    .replace('dd', dd)
    .replace('d', day)
    .replace('yyyy', yyyy)
    .replace('yy', yy)
    .replace('MM', MM)
    .replace('M', M);

    if (withTimeZone) {
      patternStr = patternStr + this.getTimezone(date);
    }

    return patternStr;
  }

  public getDateTimeFormatPattern(isLineChart: boolean, data: any, language: string, chartSettings) {
    if (!data || data.length === 0) {
      return 'short';
    }

    if (!isLineChart) {
      const values = Object.values(data)?.[0] as any;
      const start = values?.[0]?.[0];
      const end = values?.[values?.length - 1]?.[0];
      const duration = Math.abs(end - start);
      const oneHourInMilliseconds = 60 * 60 * 1000;
      if (duration < 12 * oneHourInMilliseconds) {
        return 'medium';
      }
      return 'short';
    }


    const start = data[0].ts;
    const end = data[data.length - 1].ts;
    const duration = Math.abs(end - start);

    const oneHourInMilliseconds = 60 * 60 * 1000;
    if (duration < 24 * oneHourInMilliseconds) {
      return 'mediumTime';
    }
    return 'short';
  }

  public determineLineChartAxisColor(axis, isLineChart: boolean) {
    const defaultColor = ChartConstants.DEFAULT_LINE_COLOR;
    if (!axis) {
      return defaultColor;
    }

    const signals = axis.signals;
    if (!signals || signals.length === 0 || signals.length > 1) {
      return defaultColor;
    }

    const signal = signals[0];
    if (!signal.colors || !signal.colors.main) {
      return defaultColor;
    }

    return this.createHex(signal.colors.main, isLineChart);
  }

  private getPrecision(value, type) {
    if (type === DATA_TYPE.BOOLEAN || type === DATA_TYPE.BOOL) {
      return 2; // Bools should always have the precision 2 for IDA or line chart -> Tooltip!
    } else if (!isNaN(parseFloat(value)) && !isNaN(value - 0)) {
      return value;
    }
    return 1;
  }

  private twoDigitPad(num) {
    return num < 10 ? '0' + num : num;
  }

  private getTimezone(date: Date): string {
    const timeZone = date.getTimezoneOffset() / 60;
    if (timeZone === 0) {
      return '(GMT+00:00)';
    }
    const twoDigitPad = this.twoDigitPad(Math.abs(timeZone));
    return timeZone > 0 ? `(GMT-${twoDigitPad}:00)` : `(GMT+${twoDigitPad}:00)`;
  }

  public getLineWidgetMachineSignalById(id, machineSignals: Signal[]) {
    if (machineSignals) {
      return Object.values(machineSignals).find(function (item: Signal) {
      return item.id === id;
    });
    }
    return '';
  }

  public getMachineSignalById(id, machineSignals: Signal[]) {
    if (machineSignals) {
      return Object.values(machineSignals).find(function (item: Signal) {
        return item.table + '@' + item.name === id;
      });
    }
    return '';
  }
}
